/**
 * DeepAnalysis — renders the 9-section comprehensive report.
 */

import { useState } from 'react';
import { BrainCircuit, FileDown, Check, Copy } from 'lucide-react';
import { api } from '../../api/client';

interface Props {
  startupId: string;
  deepAnalysis: { data?: Record<string, unknown>; generatedAt?: string; status?: string } | null;
  onGenerated: (data: Record<string, unknown>) => void;
}

const SECTIONS = [
  { key: 'executiveSummary', label: 'Executive Summary' },
  { key: 'marketAnalysis', label: 'Market Analysis' },
  { key: 'competitiveLandscape', label: 'Competitive Landscape' },
  { key: 'businessModelAnalysis', label: 'Business Model Analysis' },
  { key: 'goToMarketStrategy', label: 'Go-To-Market Strategy' },
  { key: 'riskMatrix', label: 'Risk Matrix' },
  { key: 'validationInterpretation', label: 'Validation Interpretation' },
  { key: 'investorPitchNarrative', label: 'Investor Pitch Narrative' },
  { key: 'ninetyDayRoadmap', label: '90-Day Roadmap' },
];

const C = {
  bg: '#0d0d1f',
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  green: '#22c55e',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

function renderValue(value: unknown, key?: string): React.ReactNode {
  if (typeof value === 'string') return <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{value}</p>;
  if (typeof value === 'number') return <span style={{ fontSize: 13, color: C.text }}>{value}</span>;
  if (Array.isArray(value)) {
    // Risk matrix table
    if (key === 'riskMatrix' && value.length > 0 && typeof value[0] === 'object' && value[0]?.risk) {
      return (
        <div className="dash-table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['Risk', 'Probability', 'Impact', 'Mitigation'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: C.text2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((row: any, i: number) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '8px 10px', color: C.text }}>{row.risk}</td>
                <td style={{ padding: '8px 10px', color: row.probability === 'high' ? '#ef4444' : row.probability === 'medium' ? '#f97316' : C.green }}>{row.probability}</td>
                <td style={{ padding: '8px 10px', color: row.impact === 'high' ? '#ef4444' : row.impact === 'medium' ? '#f97316' : C.green }}>{row.impact}</td>
                <td style={{ padding: '8px 10px', color: C.text2 }}>{row.mitigation}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      );
    }
    // Competitors table
    if (key === 'competitors' && value.length > 0 && typeof value[0] === 'object' && value[0]?.name) {
      return (
        <div className="dash-table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['Competitor', 'Strengths', 'Weaknesses', 'Pricing', 'How We Win'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: C.text2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((row: any, i: number) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '8px 10px', color: C.text, fontWeight: 600 }}>{row.name}</td>
                <td style={{ padding: '8px 10px', color: C.text2 }}>{Array.isArray(row.strengths) ? row.strengths.join(', ') : row.strengths}</td>
                <td style={{ padding: '8px 10px', color: C.text2 }}>{Array.isArray(row.weaknesses) ? row.weaknesses.join(', ') : row.weaknesses}</td>
                <td style={{ padding: '8px 10px', color: C.text2 }}>{row.pricing}</td>
                <td style={{ padding: '8px 10px', color: C.green }}>{row.howWeWin}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      );
    }
    // 90-day roadmap
    if (value.length > 0 && typeof value[0] === 'object' && value[0]?.month) {
      return (
        <div>
          {value.map((item: any, i: number) => (
            <div key={i} style={{ background: C.surface, borderRadius: 10, padding: '12px 16px', marginBottom: 8, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 6 }}>Month {item.month}: {item.milestone}</div>
              {Array.isArray(item.specificGoals) && item.specificGoals.map((g: string, j: number) => (
                <div key={j} style={{ fontSize: 12, color: C.text2, paddingLeft: 8 }}>• {g}</div>
              ))}
            </div>
          ))}
        </div>
      );
    }
    // Generic array
    return value.map((v, i) => (
      <div key={i} style={{ marginBottom: 4, fontSize: 13, color: C.text2 }}>
        {typeof v === 'string' ? `• ${v}` : typeof v === 'object' ? <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(v, null, 2)}</pre> : `• ${String(v)}`}
      </div>
    ));
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    // Competitive landscape with nested competitors
    if ('competitors' in obj) {
      return (
        <div>
          {obj.summary && <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.7, marginBottom: 12 }}>{String(obj.summary)}</p>}
          {Array.isArray(obj.competitors) && renderValue(obj.competitors, 'competitors')}
        </div>
      );
    }
    // Validation interpretation with verdict
    if ('verdict' in obj) {
      const verdictColor = obj.verdict === 'BUILD IT' ? C.green : obj.verdict === 'PIVOT' ? '#f97316' : '#ef4444';
      return (
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: verdictColor, marginBottom: 12 }}>{String(obj.verdict)}</div>
          {obj.dataReading && <p style={{ fontSize: 13, color: C.text2, lineHeight: 1.7, marginBottom: 12 }}>{String(obj.dataReading)}</p>}
          {Array.isArray(obj.keyInsights) && obj.keyInsights.map((v: string, i: number) => (
            <div key={i} style={{ fontSize: 13, color: C.text2, marginBottom: 4 }}>• {v}</div>
          ))}
          {Array.isArray(obj.recommendations) && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: C.primary, marginBottom: 6 }}>RECOMMENDATIONS</p>
              {obj.recommendations.map((v: string, i: number) => (
                <div key={i} style={{ fontSize: 13, color: C.text2, marginBottom: 4 }}>• {v}</div>
              ))}
            </div>
          )}
        </div>
      );
    }
    // Generic object — render key-value pairs
    return Object.entries(obj).map(([k, v]) => (
      <div key={k} style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
        </span>
        <div style={{ marginTop: 2 }}>{renderValue(v)}</div>
      </div>
    ));
  }
  return <span style={{ color: C.text2, fontSize: 13 }}>{String(value)}</span>;
}

export default function DeepAnalysis({ startupId, deepAnalysis, onGenerated }: Props) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(SECTIONS.map(s => s.key)));
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  const data = (deepAnalysis?.data ?? {}) as Record<string, unknown>;
  const hasData = Object.keys(data).length > 0;

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const { data: res } = await api.post(`/startups/${startupId}/deep-analysis`);
      onGenerated(res.data.deepAnalysis);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || 'Generation failed. Your data is safe.';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = () => {
    if (hasData) {
      setShowRegenConfirm(true);
    } else {
      generate();
    }
  };

  const toggleSection = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const copySection = (key: string, content: unknown) => {
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const copyFullReport = () => {
    const report = SECTIONS.map(s => {
      const content = data[s.key];
      if (!content) return '';
      return `## ${s.label}\n\n${typeof content === 'string' ? content : JSON.stringify(content, null, 2)}`;
    }).filter(Boolean).join('\n\n---\n\n');
    navigator.clipboard.writeText(report).catch(() => {});
    setCopiedKey('_full');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (!hasData) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}><BrainCircuit size={48} color={C.primary} /></div>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 8 }}>
          Ready to generate your Deep Analysis
        </h3>
        <p style={{ fontSize: 14, color: C.text2, maxWidth: 400, margin: '0 auto 24px', lineHeight: 1.6 }}>
          Synthesizes all your pipeline data and validation results into a comprehensive investor-grade report. Takes 60–90 seconds.
        </p>
        {error && (
          <div style={{ color: '#ff4466', fontSize: 13, marginBottom: 12 }}>
            {error}
            <button onClick={generate} style={{ marginLeft: 8, color: C.primary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Try again</button>
          </div>
        )}
        <button
          onClick={generate}
          disabled={generating}
          style={{
            background: 'linear-gradient(135deg, #7c6ff7, #00d4ff)',
            border: 'none', borderRadius: 12, color: '#fff',
            fontSize: 15, fontWeight: 700, padding: '12px 32px', cursor: generating ? 'not-allowed' : 'pointer',
            opacity: generating ? 0.6 : 1,
          }}
        >
          {generating ? 'Generating analysis...' : '▶ Generate Deep Analysis'}
        </button>
        <p style={{ fontSize: 12, color: C.text3, marginTop: 8 }}>Included in Pro · No credits charged</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}><BrainCircuit size={20} /> Deep Analysis</h3>
          {deepAnalysis?.generatedAt && (
            <span style={{ fontSize: 11, color: C.text2 }}>
              Generated on {new Date(deepAnalysis.generatedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={handleRegenerate}
            disabled={generating}
            style={{
              background: 'rgba(124,111,247,0.1)', border: '1px solid rgba(124,111,247,0.3)',
              borderRadius: 8, color: C.primary, fontSize: 12, fontWeight: 600,
              padding: '5px 12px', cursor: generating ? 'not-allowed' : 'pointer',
            }}
          >
            {generating ? 'Regenerating...' : '🔄 Regenerate'}
          </button>
          <button
            onClick={copyFullReport}
            style={{
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
              borderRadius: 8, color: copiedKey === '_full' ? C.green : C.text2, fontSize: 12,
              padding: '5px 12px', cursor: 'pointer',
            }}
          >
            {copiedKey === '_full' ? <><Check size={12} /> Report copied as Markdown</> : <><FileDown size={14} /> Export Full Report</>}
          </button>
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map(section => {
        const content = data[section.key];
        if (!content) return null;
        const isOpen = expanded.has(section.key);

        return (
          <div key={section.key} style={{
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 12, marginBottom: 8, overflow: 'hidden',
          }}>
            <button
              onClick={() => toggleSection(section.key)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: 'transparent', border: 'none',
                color: C.text, cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}
            >
              <span>{section.label}</span>
              <span style={{ color: C.text2, fontSize: 12 }}>{isOpen ? '▾' : '▸'}</span>
            </button>

            {isOpen && (
              <div style={{ padding: '0 16px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <button
                    onClick={() => copySection(section.key, content)}
                    style={{
                      background: 'transparent', border: `1px solid ${copiedKey === section.key ? C.green : C.border}`,
                      borderRadius: 6, color: copiedKey === section.key ? C.green : C.text2,
                      fontSize: 11, padding: '3px 8px', cursor: 'pointer',
                    }}
                  >
                    {copiedKey === section.key ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy section</>}
                  </button>
                </div>
                {renderValue(content, section.key)}
              </div>
            )}
          </div>
        );
      })}

      {/* Regenerate confirmation */}
      {showRegenConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowRegenConfirm(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#0d0d1f', border: `1px solid ${C.border}`,
            borderRadius: 18, padding: '28px 32px', maxWidth: 400, textAlign: 'center',
          }}>
            <p style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Regenerate Deep Analysis?</p>
            <p style={{ color: C.text2, fontSize: 13, marginBottom: 20 }}>This will replace your current report. Continue?</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowRegenConfirm(false)} style={{
                flex: 1, padding: '10px', borderRadius: 10, background: 'transparent',
                border: `1px solid ${C.border}`, color: C.text2, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={() => { setShowRegenConfirm(false); generate(); }} style={{
                flex: 1, padding: '10px', borderRadius: 10, background: C.primary,
                border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer',
              }}>Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
